import React, { useState, useEffect, useContext } from "react";
import StaticPage from "../../common/screen/StaticPage";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Backdrop, Container, CircularProgress, Grid, Typography } from "@mui/material";
import bkstApi from "../../common/api";
import { useStripe } from "@stripe/react-stripe-js";
import { AppStateContext } from "../../common/context/AppStateContext";

export default function PaymentStatus(props) {
  const stripe = useStripe();
  const { resetCartState } = useContext(AppStateContext);

  const [status, setStatus] = useState("");
  const [orderId, setOrderId] = useState("");

  // Parse URL parameters
  const params = new URLSearchParams(window.location.search);
  const urlOrderId = params.get("order_id") || "";
  const cartId = params.get("cart_id") || "";

  // Stripe-specific params
  const clientSecret = params.get("payment_intent_client_secret") || "";

  // Rainforest-specific params
  const payinId = params.get("payin_id") || "";
  const rfPaymentStatus = params.get("status") || ""; // approved, declined, error

  // Determine payment provider based on URL params
  const isRainforest = !!payinId;
  const isStripe = !!clientSecret;

  useEffect(() => {
    if (isRainforest) {
      // Rainforest flow - check status from URL param
      handleRainforestStatus();
    } else if (isStripe && stripe) {
      // Stripe flow - retrieve payment intent status
      handleStripeStatus();
    }
  }, [stripe, isRainforest, isStripe, payinId]);

  const handleRainforestStatus = () => {
    if (rfPaymentStatus === "approved") {
      checkPaymentStatus();
    } else if (rfPaymentStatus === "declined" || rfPaymentStatus === "error") {
      setStatus("fail");
    } else if (urlOrderId) {
      // We already have an order ID, just verify
      checkPaymentStatus();
    } else {
      setStatus("unknown");
    }
  };

  const handleStripeStatus = () => {
    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent.status) {
        case "requires_capture":
        case "succeeded":
          checkPaymentStatus();
          break;
        case "processing":
          setStatus("timeout");
          break;
        case "requires_payment_method":
          setStatus("fail");
          break;
        default:
          setStatus("unknown");
          break;
      }
    });
  };

  const checkPaymentStatus = () => {
    setStatus("processing");

    // Clear cart state since payment was successful
    if (cartId) {
      resetCartState();
    }

    let attempts = 0;
    const maxAttempts = 14;

    const pollInterval = setInterval(() => {
      attempts++;

      if (attempts > maxAttempts) {
        clearInterval(pollInterval);
        setStatus("timeout");
        return;
      }

      // Poll server to check if order has been created
      bkstApi(`/get-payment-status?orderId=${urlOrderId}&cartId=${cartId}`)
        .then((res) => {
          if (res.data?.orderId) {
            clearInterval(pollInterval);
            setOrderId(res.data.orderId);
            setStatus("success");
          }
          // Continue polling if no order ID yet
        })
        .catch((err) => {
          console.error("Error checking payment status:", err);
          // Continue polling on error
        });
    }, 1000);

    // Note: cleanup should be handled by component unmount, but this function
    // doesn't return a cleanup function since it's not a useEffect
  };

  // Loading/Processing state
  if (!status || status === "processing") {
    return (
      <Container style={{ height: "100vh" }}>
        <Backdrop open={true} style={{ zIndex: "1000", color: "#fff" }}>
          <Grid container spacing={2} align="center">
            <Grid item xs={12}>
              <CircularProgress color="inherit" />
            </Grid>
            <Grid item xs={12}>
              <Typography>
                {status === "processing" ? "Updating your order..." : "Checking payment status..."}
              </Typography>
            </Grid>
          </Grid>
        </Backdrop>
      </Container>
    );
  }

  // Success state
  if (status === "success") {
    return (
      <StaticPage
        title="Payment Successful!"
        desc={`Your order has been confirmed. ${orderId ? `Order ID: ${orderId}` : ""} You will receive a confirmation email shortly.`}
        icon={CheckCircleOutlineOutlinedIcon}
      />
    );
  }

  // Timeout state - payment likely went through but order creation is slow
  if (status === "timeout") {
    return (
      <StaticPage
        title="Waiting for confirmation"
        desc="We're still processing your order. Once complete, we'll notify you via email & text. This may take a few minutes."
        icon={CheckCircleOutlineOutlinedIcon}
      />
    );
  }

  // Fail state
  if (status === "fail") {
    return (
      <StaticPage
        title="Payment Failed"
        desc="Please try again. If your payment fails again, please use a different payment method or contact support."
        icon={ErrorOutlineIcon}
      />
    );
  }

  // Unknown state
  return (
    <StaticPage
      title="Unknown Status"
      desc="We couldn't determine your payment status. Please check your email for confirmation or contact support."
      icon={ErrorOutlineIcon}
    />
  );
}
